/**
 * Phase 6e.3 — audit accordion for the post-sync report.
 *
 * Reads `engagement.lastFxSync` (FxSyncAudit | null) plus the latest
 * `plan.contractUpdates` if available via a transient prop, and
 * renders a collapsible "Latest Sync Report" panel. Starts COLLAPSED
 * — auditors opt in to seeing the detail.
 *
 * If `engagement.lastFxSync` is `null` (or missing), the component
 * returns null — nothing renders on the page.
 *
 * Per-contract detail is best-effort: when the Magic Button passes
 * the latest `contractUpdates` via the optional prop, we render one
 * row per FX contract with rate counts + per-error reasons. When the
 * prop is absent (e.g. page reload, accordion reading purely from
 * persisted audit), we fall back to summary-only mode.
 */

import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { EngagementHistoryEntry } from "@/types";

import type { ContractFxUpdate } from "./types";

export interface FxSyncAuditAccordionProps {
  engagement: EngagementHistoryEntry;
  /**
   * Optional per-contract detail from the latest sync. When the Magic
   * Button just ran a sync, it hands the matcher's `contractUpdates`
   * in here so the accordion can render the green/yellow/red rows.
   * When omitted, only the summary panel is shown.
   */
  contractUpdates?: ContractFxUpdate[];
}

export function FxSyncAuditAccordion({
  engagement,
  contractUpdates,
}: FxSyncAuditAccordionProps) {
  const { t } = useTranslation();
  const audit = engagement.lastFxSync;
  if (!audit) return null;

  return (
    <Accordion
      type="single"
      collapsible
      data-testid="fx-sync-audit-accordion"
      // Starts collapsed — auditors opt in to seeing the detail.
    >
      <AccordionItem value="fx-sync-audit">
        <AccordionTrigger data-testid="fx-sync-audit-trigger">
          {t("fx.sync.accordion.title")}
        </AccordionTrigger>
        <AccordionContent>
          <div className="space-y-4">
            <Card
              className="p-3 text-sm space-y-1"
              data-testid="fx-sync-audit-summary"
            >
              <p className="flex items-center gap-2">
                <CheckCircle2
                  className="h-4 w-4 text-emerald-600"
                  aria-hidden="true"
                />
                {t("fx.sync.accordion.summary.matched", {
                  count: audit.totalRatesMatched,
                })}
              </p>
              <p className="flex items-center gap-2">
                <AlertTriangle
                  className="h-4 w-4 text-amber-600"
                  aria-hidden="true"
                />
                {t("fx.sync.accordion.summary.t1Fallbacks", {
                  count: audit.totalT1Fallbacks,
                })}
              </p>
              <p className="flex items-center gap-2">
                <XCircle
                  className="h-4 w-4 text-destructive"
                  aria-hidden="true"
                />
                {t("fx.sync.accordion.summary.unmatched", {
                  count: audit.totalUnmatched,
                })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("fx.sync.accordion.summary.source", { source: audit.source })}
                {" · "}
                {t("fx.sync.accordion.summary.fetchedAt", {
                  timestamp: audit.fetchedAt,
                })}
              </p>
            </Card>

            {contractUpdates && contractUpdates.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">
                  {t("fx.sync.accordion.contracts.title")}
                </h4>
                <ul
                  className="space-y-1.5"
                  data-testid="fx-sync-audit-contracts"
                >
                  {contractUpdates.map((u) => (
                    <ContractRow key={u.contractId} update={u} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function ContractRow({ update }: { update: ContractFxUpdate }) {
  const { t } = useTranslation();
  const totalFilled =
    (update.commencementSpotRate ? 1 : 0) +
    update.historicalRates.length +
    update.paymentDateRates.length;
  const totalT1 =
    (update.commencementSpotRate &&
    update.commencementSpotRate.fellBackDays > 0
      ? 1
      : 0) +
    update.historicalRates.filter((r) => r.fellBackDays > 0).length +
    update.paymentDateRates.filter((r) => r.fellBackDays > 0).length;
  const errorCount = update.errors.length;

  const variant: "success" | "warning" | "danger" =
    totalFilled === 0
      ? "danger"
      : errorCount > 0 || totalT1 > 0
        ? "warning"
        : "success";

  const Icon =
    variant === "success"
      ? CheckCircle2
      : variant === "warning"
        ? AlertTriangle
        : XCircle;
  const iconClass =
    variant === "success"
      ? "text-emerald-600"
      : variant === "warning"
        ? "text-amber-600"
        : "text-destructive";

  return (
    <li
      className="rounded-md border p-2 text-sm space-y-1"
      data-testid={`fx-sync-audit-contract-${update.contractId}`}
      data-variant={variant}
    >
      <div className="flex items-center gap-2">
        <Icon className={cn("h-4 w-4 shrink-0", iconClass)} aria-hidden="true" />
        <span className="font-mono">{update.contractId}</span>
        <span className="text-muted-foreground">({update.currency})</span>
      </div>
      <div className="pl-6 text-xs text-muted-foreground">
        {totalFilled > 0 ? (
          <>
            {t("fx.sync.accordion.contract.rateCount", { count: totalFilled })}
            {totalT1 > 0 && (
              <>
                {" "}
                {t("fx.sync.accordion.contract.t1Count", { count: totalT1 })}
              </>
            )}
            {errorCount > 0 && (
              <>
                {" · "}
                {t("fx.sync.accordion.contract.errorCount", {
                  count: errorCount,
                })}
              </>
            )}
            {errorCount === 0 && totalT1 === 0 && (
              <>
                {" · "}
                {t("fx.sync.accordion.contract.noErrors")}
              </>
            )}
          </>
        ) : (
          t("fx.sync.accordion.contract.errorCount", { count: errorCount })
        )}
      </div>
      {update.errors.length > 0 && (
        <ul className="pl-6 text-xs space-y-0.5">
          {update.errors.map((e, idx) => {
            // `ContractFxError.reason` is an enum CODE
            // (`NO_RATE_AVAILABLE` etc.), not human-readable copy —
            // so we always render through i18next. If no translation
            // is found, i18next returns the key itself, which still
            // beats showing the raw enum name.
            const reasonText = (
              t as unknown as (
                key: string,
                params?: Record<string, string | number>,
              ) => string
            )(e.reasonI18nKey, e.reasonParams ?? {});
            return (
              <li
                key={`${update.contractId}-${e.field}-${e.intendedDate}-${idx}`}
                className="text-muted-foreground"
              >
                • {e.intendedDate} {e.field}: {reasonText}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
